import { FunctionComponent } from 'react';
import { IconButton, Tooltip, IconButtonProps } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useTranslation } from '@/lib';
import useClipboard from '@/hooks/useClipboard';
import { toast } from 'react-toastify';

interface CopyToClipboardButtonProps {
  /**
   * The content to copy to clipboard
   */
  copyContent: string;
  /**
   * The tooltip text to display on hover
   * @default "Copy to clipboard"
   */
  tooltipText?: string;
  /**
   * The size of the icon button
   * @default "small"
   */
  size?: IconButtonProps['size'];
  /**
   * The size of the icon
   * @default "small"
   */
  iconSize?: 'inherit' | 'small' | 'medium' | 'large';
  /**
   * Optional CSS class name
   */
  className?: string;
  /**
   * Custom success message to display after copying
   * @default "Copied to clipboard"
   */
  successMessage?: string;
}

export const CopyToClipboardButton: FunctionComponent<CopyToClipboardButtonProps> = ({
  copyContent,
  tooltipText,
  size = 'small',
  iconSize = 'small',
  className,
  successMessage,
}) => {
  const { t } = useTranslation();
  const { copyToClipboard } = useClipboard();

  const handleCopy = () => {
    copyToClipboard(copyContent);
    toast.success(successMessage || t('copied_to_clipboard'));
  };

  return (
    <Tooltip title={tooltipText || t('copy_to_clipboard')}>
      <IconButton size={size} onClick={handleCopy} className={className}>
        <ContentCopy fontSize={iconSize} />
      </IconButton>
    </Tooltip>
  );
};
